import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { useParams } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  usePersonalDetails,
  useSavePersonalDetails,
} from "../../../api/queries/profileQueries";
import { useCheckCompleteness } from "../../../api/queries/employeeQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { SectionToolbar, EditActions, SubHeader } from "./ProfileUI";

const EmployeePersonalDetails = forwardRef((props, ref) => {
  const { id: employeeId } = useParams();
  // `employee` = this employee's personal-details record (null until created)
  const { data: employee } = usePersonalDetails(employeeId);
  const savePersonalDetails = useSavePersonalDetails(employeeId);
  const checkCompleteness = useCheckCompleteness();

  const [isEditing, setIsEditing] = useState(true); // Default to true based on requirements
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });

  const [formData, setFormData] = useState({
    gender: "",
    civilStatus: "Single",
    religion: "",
    height: "",
    weight: "",
    birthDate: "",
    birthPlace: "",
    mothersMaidenName: "",
    contactNumber: "",
    alternateContactNumber: "",
  });

  const religionOptions = [
    "Roman Catholic",
    "Islam",
    "Iglesia ni Cristo",
    "Christian Bible Church",
    "Seventh-day Adventist",
    "Jehovahs Witnesses", // backend enum spelling (no apostrophe)
    "Baptist",
    "Pentecostal",
    "Anglican / Episcopal",
    "LDS (Mormon)",
    "Buddhism",
    "Hinduism",
    "Sikhism",
    "Judaism",
    "Atheist",
    "Agnostic",
    "Others",
    "Prefer not to say",
  ];

  const calculatedAge = useMemo(() => {
    if (!formData.birthDate) return "";
    const today = new Date();
    const birthDate = new Date(formData.birthDate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 0 ? age : "";
  }, [formData.birthDate]);

  useEffect(() => {
    if (employee) {
      const formattedBirthDate = employee.birthDate
        ? employee.birthDate.split("T")[0]
        : "";

      setFormData({
        gender: employee.gender ?? "",
        civilStatus: employee.civilStatus ?? "Single",
        religion: employee.religion ?? "",
        height: employee.height ?? "",
        weight: employee.weight ?? "",
        birthDate: formattedBirthDate,
        birthPlace: employee.birthPlace ?? "",
        mothersMaidenName: employee.mothersMaidenName ?? "",
        contactNumber: employee.contactNumber ?? "",
        alternateContactNumber:
          employee.alternateContactNumber ?? employee.alternateContactNo ?? "",
      });
      setIsEditing(false);
    }
  }, [employee]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      return await handleSubmit();
    },
  }));

  const validate = () => {
    let newErrors = {};
    if (!formData.gender) newErrors.gender = true;
    if (!formData.mothersMaidenName?.trim()) newErrors.mothersMaidenName = true;

    if (!formData.contactNumber || formData.contactNumber.trim() === "") {
      newErrors.contactNumber = true;
    } else if (!isValidPhoneNumber(formData.contactNumber)) {
      newErrors.contactNumberInvalid = true;
    }

    if (
      formData.alternateContactNumber &&
      formData.alternateContactNumber.trim() !== ""
    ) {
      if (!isValidPhoneNumber(formData.alternateContactNumber)) {
        newErrors.alternateContactNumberInvalid = true;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handlePhoneChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value || "" }));
    if (name === "contactNumber") {
      setErrors((prev) => ({
        ...prev,
        contactNumber: false,
        contactNumberInvalid: false,
      }));
    }
    if (name === "alternateContactNumber") {
      setErrors((prev) => ({ ...prev, alternateContactNumberInvalid: false }));
    }
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handleCancel = () => {
    if (employee) {
      const formattedBirthDate = employee.birthDate
        ? employee.birthDate.split("T")[0]
        : "";

      setFormData({
        gender: employee.gender ?? "",
        civilStatus: employee.civilStatus ?? "Single",
        religion: employee.religion ?? "",
        height: employee.height ?? "",
        weight: employee.weight ?? "",
        birthDate: formattedBirthDate,
        birthPlace: employee.birthPlace ?? "",
        mothersMaidenName: employee.mothersMaidenName ?? "",
        contactNumber: employee.contactNumber ?? "",
        alternateContactNumber:
          employee.alternateContactNumber ?? employee.alternateContactNo ?? "",
      });
    }
    setIsEditing(false);
    setErrors({});
    setStatusMsg({ text: "", type: "" });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      setStatusMsg({ text: "Missing required fields.", type: "danger" });
      return;
    }

    setIsSaving(true);
    setStatusMsg({ text: "", type: "" });

    try {
      const payload = {
        ...formData,
        civilStatus: formData.civilStatus,
        birthDate: formData.birthDate === "" ? null : formData.birthDate,
        employeeId,
        alternateContactNo: formData.alternateContactNumber,
      };

      // Creates when there's no record yet; refreshes the section afterwards
      await savePersonalDetails.mutateAsync({
        id: employee?.id,
        data: payload,
      });
      // Recalculates Complete/Incomplete and refreshes the employee record
      await checkCompleteness.mutateAsync(employeeId);

      setStatusMsg({ text: "Saved successfully!", type: "success" });
      setIsEditing(false);

      setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    } catch (err) {
      setStatusMsg({
        text: getApiErrorMessage(err, "Failed to save."),
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Field classes for view / edit / error states (see profile.css)
  const fieldClass = (name) =>
    `field-control ${!isEditing ? "is-readonly" : ""} ${isEditing && errors[name] ? "has-error" : ""}`;

  return (
    <div className="position-relative">
      <div>
        {isSaving && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <SectionToolbar statusMsg={statusMsg}>
          <EditActions
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onSave={handleSubmit}
            onCancel={handleCancel}
            isSaving={isSaving}
          />
        </SectionToolbar>

        <form onSubmit={handleSubmit}>
          <SubHeader title="Basic Information" icon="bi-person" />
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="profile-label">
                Gender <span className="text-danger">*</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-select form-select-sm shadow-none ${errors.gender ? "border-danger" : ""} ${fieldClass("gender")}`}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="profile-label">Civil Status</label>
              <select
                name="civilStatus"
                value={formData.civilStatus}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-select form-select-sm shadow-none ${fieldClass("civilStatus")}`}
              >
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Widowed">Widowed</option>
                <option value="Divorced">Divorced</option>
                <option value="Separated"> Separated</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="profile-label">Religion</label>
              <select
                name="religion"
                value={formData.religion}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-select form-select-sm shadow-none ${fieldClass("religion")}`}
              >
                <option value="">Select Religion</option>
                {religionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 col-lg-3">
              <label className="profile-label">Birth Date</label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-control form-control-sm shadow-none ${fieldClass("birthDate")}`}
              />
            </div>
            <div className="col-md-2 col-lg-2 text-center">
              <label className="profile-label">Age</label>
              <div className="form-control form-control-sm text-center fw-bold field-control field-computed">
                {calculatedAge || "--"}
              </div>
            </div>
            <div className="col-md-6 col-lg-3">
              <label className="profile-label">Birth Place</label>
              <input
                type="text"
                name="birthPlace"
                value={formData.birthPlace}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-control form-control-sm shadow-none ${fieldClass("birthPlace")}`}
                placeholder="City/Province"
              />
            </div>
            <div className="col-md-12 col-lg-4">
              <label className="profile-label">
                Mother's Maiden Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="mothersMaidenName"
                value={formData.mothersMaidenName}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-control form-control-sm shadow-none ${errors.mothersMaidenName ? "border-danger" : ""} ${fieldClass("mothersMaidenName")}`}
                placeholder="Maiden Name"
              />
            </div>
          </div>

          <SubHeader title="Physical & Contact" icon="bi-telephone" />
          <div className="row g-3">
            <div className="col-6 col-md-3 col-lg-2">
              <label className="profile-label">Height (cm)</label>
              <input
                type="number"
                name="height"
                value={formData.height}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-control form-control-sm shadow-none ${fieldClass("height")}`}
                placeholder="e.g. 175"
              />
            </div>
            <div className="col-6 col-md-3 col-lg-2">
              <label className="profile-label">Weight (kg)</label>
              <input
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-control form-control-sm shadow-none ${fieldClass("weight")}`}
                placeholder="e.g. 70"
              />
            </div>

            <div className="col-md-6 col-lg-4">
              <label className="profile-label">
                Main Contact Number <span className="text-danger">*</span>
              </label>
              <PhoneInput
                className={`PhoneInput phone-field ${isEditing ? "is-editing" : ""} ${errors.contactNumber || errors.contactNumberInvalid ? "has-error" : ""}`}
                placeholder="Main contact"
                value={formData.contactNumber}
                onChange={(val) => handlePhoneChange("contactNumber", val)}
                disabled={!isEditing}
                defaultCountry="PH"
                international
              />
              {errors.contactNumberInvalid && (
                <span className="text-danger extra-small">
                  Invalid phone number format.
                </span>
              )}
            </div>
            <div className="col-md-12 col-lg-4">
              <label className="profile-label">
                Alternate Contact (Optional)
              </label>
              <PhoneInput
                className={`PhoneInput phone-field ${isEditing ? "is-editing" : ""} ${errors.alternateContactNumberInvalid ? "has-error" : ""}`}
                placeholder="Optional"
                value={formData.alternateContactNumber}
                onChange={(val) =>
                  handlePhoneChange("alternateContactNumber", val)
                }
                disabled={!isEditing}
                defaultCountry="PH"
                international
              />
              {errors.alternateContactNumberInvalid && (
                <span className="text-danger extra-small">
                  Invalid phone number format.
                </span>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default EmployeePersonalDetails;
